import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../api'

export default function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'resend'>('verifying')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSent, setResendSent] = useState(false)

  // Guard so verification only ever runs once
  const hasVerified = useRef(false)

  useEffect(() => {
    if (hasVerified.current) return

    const token = searchParams.get('token')

    if (!token) {
      setStatus('resend')
      return
    }

    hasVerified.current = true

    const verify = async () => {
      try {
        const res = await api.get('/auth/verify-email?token=' + token)
        setStatus('success')
        setMessage(res.data.message || 'Your email has been verified.')
        setTimeout(() => navigate('/login'), 3000)
      } catch (err: any) {
        const serverMessage = err?.response?.data?.error || ''

        // If already verified, treat as success
        if (serverMessage.toLowerCase().includes('already')) {
          setStatus('success')
          setMessage('Your email is already verified.')
          setTimeout(() => navigate('/login'), 3000)
          return
        }

        setStatus('error')
        setMessage(serverMessage || 'Verification failed')
      }
    }

    verify()
  }, [searchParams, navigate])

  const handleResend = async () => {
    if (!email || resendLoading) return
    setResendLoading(true)
    try {
      await api.post('/auth/resend-verification', { email })
      setResendSent(true)
    } catch (err) {
      console.error('Resend failed', err)
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--cream)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '440px', textAlign: 'center' }}>

        <div
          onClick={() => navigate('/')}
          style={{
            fontFamily: 'var(--font-display)', fontSize: '28px',
            fontWeight: 700, color: 'var(--navy)',
            marginBottom: '40px', cursor: 'pointer',
          }}
        >
          Velu<span style={{ color: 'var(--gold)' }}>mio</span>
        </div>

        <div style={{
          background: 'var(--white)', borderRadius: 'var(--radius-lg)',
          padding: '48px 40px', boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--cream-dark)',
        }}>

          {status === 'verifying' && (
            <>
              <div style={{
                width: '48px', height: '48px',
                border: '3px solid var(--warm-border)',
                borderTopColor: 'var(--gold)', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 24px',
              }} />
              <h2 style={{
                fontFamily: 'var(--font-display)', fontSize: '22px',
                color: 'var(--navy)', marginBottom: '8px',
              }}>
                Verifying your email
              </h2>
              <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
                This will only take a moment.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>✅</div>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontSize: '24px',
                color: 'var(--navy)', marginBottom: '10px',
              }}>
                Email verified
              </h2>
              <p style={{
                fontSize: '15px', color: 'var(--muted)',
                lineHeight: 1.7, marginBottom: '28px',
              }}>
                {message} You can now log in and complete your setup.
              </p>
              <button
                onClick={() => navigate('/login')}
                style={{
                  width: '100%', padding: '14px', fontSize: '15px',
                  fontWeight: 600, color: 'var(--white)',
                  background: 'var(--navy)', border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer', fontFamily: 'var(--font-body)',
                }}
              >
                Continue to login
              </button>
              <p style={{
                fontSize: '12px', color: 'var(--muted)', marginTop: '14px',
              }}>
                Redirecting automatically in a moment.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>⚠️</div>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontSize: '22px',
                color: 'var(--navy)', marginBottom: '10px',
              }}>
                Verification link not valid
              </h2>
              <p style={{
                fontSize: '14px', color: 'var(--muted)',
                lineHeight: 1.7, marginBottom: '28px',
              }}>
                {message} Links expire after 24 hours and can only be used once.
                Request a fresh one below, or try logging in — your account may
                already be verified.
              </p>
              <button
                onClick={() => setStatus('resend')}
                style={{
                  width: '100%', padding: '14px', fontSize: '15px',
                  fontWeight: 600, color: 'var(--navy)',
                  background: 'var(--gold)', border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer', fontFamily: 'var(--font-body)',
                  marginBottom: '12px',
                }}
              >
                Send me a new link
              </button>
              <button
                onClick={() => navigate('/login')}
                style={{
                  width: '100%', padding: '14px', fontSize: '15px',
                  fontWeight: 500, color: 'var(--ink-soft)',
                  background: 'transparent',
                  border: '1.5px solid var(--warm-border)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer', fontFamily: 'var(--font-body)',
                }}
              >
                Try logging in instead
              </button>
            </>
          )}

          {status === 'resend' && (
            <>
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>✉️</div>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontSize: '22px',
                color: 'var(--navy)', marginBottom: '10px',
              }}>
                Verify your email
              </h2>
              {resendSent ? (
                <p style={{
                  fontSize: '15px', color: 'var(--green)', lineHeight: 1.7,
                }}>
                  A new verification link has been sent. Please check your inbox.
                </p>
              ) : (
                <>
                  <p style={{
                    fontSize: '14px', color: 'var(--muted)',
                    lineHeight: 1.7, marginBottom: '24px',
                  }}>
                    Enter your email address and we will send you a new
                    verification link.
                  </p>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    style={{
                      width: '100%', padding: '12px 14px', fontSize: '14px',
                      border: '1.5px solid var(--warm-border)',
                      borderRadius: 'var(--radius-md)', outline: 'none',
                      fontFamily: 'var(--font-body)', color: 'var(--ink)',
                      background: 'var(--cream)', marginBottom: '16px',
                      boxSizing: 'border-box' as const,
                    }}
                  />
                  <button
                    onClick={handleResend}
                    disabled={resendLoading || !email}
                    style={{
                      width: '100%', padding: '14px', fontSize: '15px',
                      fontWeight: 600, color: 'var(--white)',
                      background: resendLoading || !email
                        ? 'var(--muted)' : 'var(--navy)',
                      border: 'none', borderRadius: 'var(--radius-md)',
                      cursor: resendLoading || !email
                        ? 'not-allowed' : 'pointer',
                      fontFamily: 'var(--font-body)',
                    }}
                  >
                    {resendLoading ? 'Sending...' : 'Send verification link'}
                  </button>
                </>
              )}
            </>
          )}

        </div>

        <p style={{
          marginTop: '24px', fontSize: '14px', color: 'var(--muted)',
        }}>
          Already verified?{' '}
          <span
            onClick={() => navigate('/login')}
            style={{ color: 'var(--gold)', cursor: 'pointer', fontWeight: 500 }}
          >
            Sign in
          </span>
        </p>

      </div>
    </div>
  )
}